import React from 'react';
import { Image as ImageIcon, Tag, Hash, Globe, FileText } from '@phosphor-icons/react';
import MediaPickerInput from '../MediaPickerInput';

export default function CommonFields({ formData, onChange, contentType }) {
  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    handleChange('tags', tags);
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder={`Enter ${contentType} title...`}
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-lg font-semibold"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        />
      </div>

      {/* Featured Image */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <ImageIcon size={16} />
          Featured Image
        </label>
        <MediaPickerInput
          value={formData.featured_image || ''}
          onChange={(url) => handleChange('featured_image', url)}
          accept="image/*"
          label="Select featured image"
          helpText="Recommended: 1200x630px"
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Tag size={16} />
          Category
        </label>
        <select
          value={formData.category || ''}
          onChange={(e) => handleChange('category', e.target.value)}
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        >
          <option value="">Select category...</option>
          <option value="Poetry">Poetry</option>
          <option value="Prose">Prose</option>
          <option value="Fiction">Fiction</option>
          <option value="Non-Fiction">Non-Fiction</option>
          <option value="Editorial">Editorial</option>
          <option value="Opinion">Opinion</option>
          <option value="Literature">Literature</option>
          <option value="Culture">Culture</option>
        </select>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Hash size={16} />
          Tags
        </label>
        <input
          type="text"
          value={(formData.tags || []).join(', ')}
          onChange={handleTagsChange}
          placeholder="Enter tags separated by commas..."
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Separate tags with commas (e.g., love, nature, inspiration)
        </p>
      </div>

      {/* Language */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Globe size={16} />
          Language
        </label>
        <select
          value={formData.language || 'hindi'}
          onChange={(e) => handleChange('language', e.target.value)}
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        >
          <option value="hindi">Hindi</option>
          <option value="english">English</option>
          <option value="hinglish">Hinglish</option>
          <option value="urdu">Urdu</option>
        </select>
      </div>

      {/* SEO Section */}
      <div className="pt-4 border-t border-border">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <FileText size={16} />
          SEO & Metadata
        </h3>
        
        {/* Meta Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Meta Title
          </label>
          <input
            type="text"
            value={formData.meta_title || ''}
            onChange={(e) => handleChange('meta_title', e.target.value)}
            placeholder="SEO title (defaults to main title if empty)"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
            maxLength={60}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {(formData.meta_title || '').length}/60 characters
          </p>
        </div>

        {/* Meta Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Meta Description
          </label>
          <textarea
            value={formData.meta_description || ''}
            onChange={(e) => handleChange('meta_description', e.target.value)}
            placeholder="Brief description for search engines..."
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm resize-none"
            rows={3}
            maxLength={160}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {(formData.meta_description || '').length}/160 characters
          </p>
        </div>

        {/* Meta Keywords */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Meta Keywords
          </label>
          <input
            type="text"
            value={(formData.meta_keywords || []).join(', ')}
            onChange={(e) => {
              const keywords = e.target.value.split(',').map(kw => kw.trim()).filter(Boolean);
              handleChange('meta_keywords', keywords);
            }}
            placeholder="SEO keywords separated by commas..."
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
          />
        </div>
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-medium mb-2">
          URL Slug
        </label>
        <input
          type="text"
          value={formData.slug || ''}
          onChange={(e) => handleChange('slug', e.target.value)}
          placeholder="Auto-generated from title"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to auto-generate from title
        </p>
      </div>
    </div>
  );
}
